import { rem } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { QueryError } from 'errors'
import FileSaver from 'file-saver'
import { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table'
import { useState } from 'react'
import { Filterable } from 'types/index'
import { NewWork, UpdateWork, Work, WorkTableItem, WorkType } from 'types/work'

export function useAddWork() {
  const queryClient = useQueryClient()
  return useMutation<Work, QueryError, NewWork>({
    mutationKey: ['addWork'],
    mutationFn: async (work) => {
      return (await axios.post('/work', work)).data
    },
    meta: { successMessage: 'Wykonana praca została dodana.', closeModal: true },
    onSuccess: (_, { workType }) => {
      queryClient.invalidateQueries({ queryKey: ['works', workType] })
      queryClient.invalidateQueries({ queryKey: ['workStatistics', workType] })
    },
  })
}

export function useUpdateWork() {
  const queryClient = useQueryClient()
  return useMutation<Work, QueryError, UpdateWork>({
    mutationKey: ['updateWork'],
    mutationFn: async (work) => {
      return (await axios.put(`/work/${work.id}`, work)).data
    },
    meta: { successMessage: 'Wykonana praca została zaktualizowana.', closeModal: true },
    onSuccess: (_, { workType }) => {
      queryClient.invalidateQueries({ queryKey: ['works', workType] })
      queryClient.invalidateQueries({ queryKey: ['workStatistics', workType] })
    },
  })
}

export function useDeleteWork() {
  const queryClient = useQueryClient()
  return useMutation<void, QueryError, { id: string; workType: WorkType }>({
    mutationKey: ['deleteWork'],
    mutationFn: async ({ id }) => {
      await axios.delete(`/work/${id}`)
    },
    meta: { successMessage: 'Wykonana praca została usunięta.' },
    onSuccess: (_, { workType }) => {
      queryClient.invalidateQueries({ queryKey: ['works', workType] })
      queryClient.invalidateQueries({ queryKey: ['workStatistics', workType] })
    },
  })
}

interface Params {
  columnFilters: MRT_ColumnFiltersState
  globalFilter: string
  sorting: MRT_SortingState
  pagination: MRT_PaginationState
  workType: WorkType
}

export function worksQuery(
  userId: string = '',
  { workType, columnFilters, globalFilter, sorting, pagination }: Params
) {
  const fetchUrl = new URL('/work', window.location.origin)
  fetchUrl.searchParams.set('skip', `${pagination.pageIndex * pagination.pageSize}`)
  fetchUrl.searchParams.set('get', `${pagination.pageSize}`)
  fetchUrl.searchParams.set('globalFilter', globalFilter ?? '')
  fetchUrl.searchParams.set('filters', JSON.stringify([...columnFilters, { id: 'workType', value: workType }]))
  fetchUrl.searchParams.set('sorting', JSON.stringify(sorting))

  return queryOptions<Filterable<Work>, QueryError, Filterable<WorkTableItem>>({
    queryKey: ['works', workType, userId, fetchUrl],
    queryFn: async () => {
      return (await axios.get(`${fetchUrl.pathname}/${userId}${fetchUrl.search}`)).data
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    select: ({ data, total }) => {
      return {
        data: data.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
        })),
        total,
      }
    },
  })
}

interface WorksParams extends Omit<Params, 'workType'> {
  workTypes: Array<WorkType>
}

export function useWorks(userId: string = '', { workTypes, ...params }: WorksParams) {
  return useQueries({
    queries: workTypes.map((workType) =>
      worksQuery(userId, {
        workType,
        ...params,
      })
    ),
  })
}

export type WorkStatistics = {
  workType: WorkType
  dataUnit: 'day' | 'month'
  from: string
  days: number
  year: number
}

export function workStatisticsQuery(userId: string, { workType, dataUnit, from, days, year }: WorkStatistics) {
  return queryOptions<Array<number>, QueryError, { workType: WorkType; data: Array<number> }>({
    queryKey:
      dataUnit === 'day'
        ? ['workStatistics', workType, userId, dataUnit, from, days]
        : ['workStatistics', workType, userId, dataUnit, year],
    queryFn: async () => {
      let path = `/work/statistics/${userId}?dataUnit=${dataUnit}&workType=${workType}`
      if (dataUnit === 'day') {
        path += `&from=${from}&days=${days}`
      } else {
        path += `&year=${year}`
      }
      return (await axios.get(path)).data
    },
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
    select: (data: Array<number>) => ({
      data: data,
      workType,
    }),
  })
}

export function useGetWorkStatistics(userId: string, props: WorkStatistics) {
  return useQuery<Array<number>, QueryError, { workType: WorkType; data: Array<number> }>(
    workStatisticsQuery(userId, props)
  )
}

export interface WorksStatistics extends Omit<WorkStatistics, 'workType'> {
  workTypes: Array<WorkType>
}

export function useGetWorksStatistics(userId: string, { workTypes, dataUnit, from, days, year }: WorksStatistics) {
  return useQueries({
    queries: workTypes.map((workType) => {
      return workStatisticsQuery(userId, {
        workType,
        dataUnit,
        from,
        days,
        year,
      })
    }),
  })
}

export function useExportWorkStatistics() {
  const [id, setId] = useState<string>()

  return useMutation<Blob, QueryError, WorkStatistics & { userId: string; fileName: string }>({
    mutationKey: ['exportWorkStatistics'],
    mutationFn: async ({ userId, dataUnit, workType, from, days, year }) => {
      let path = `/work/statistics/${userId}/export?dataUnit=${dataUnit}&workType=${workType}`
      if (dataUnit === 'day') {
        path += `&from=${from}&days=${days}`
      } else {
        path += `&year=${year}`
      }
      const id = notifications.show({
        position: 'bottom-center',
        color: 'blue',
        loading: true,
        title: 'Generowanie PDF',
        message: 'Trwa generowanie pliku PDF ze statystykami. To może potrwać kilka sekund.',
        autoClose: false,
        withCloseButton: false,
      })
      setId(id)
      await new Promise((resolve) => setTimeout(resolve, 500))
      return (await axios.get(path, { responseType: 'blob' })).data
    },
    onSuccess: (data, { fileName }) => {
      FileSaver.saveAs(data, fileName)
      notifications.update({
        id,
        color: 'green',
        title: 'PDF został wygenerowany',
        message: 'Plik PDF został wygenerowany i zapisany na dysku.',
        icon: <IconCheck style={{ width: rem(20), height: rem(20) }} />,
        loading: false,
        autoClose: 3000,
      })
    },
  })
}
